/**
 * Stats dashboard (presentational) — the JUICY redesign (owner spec, 2026-07-08):
 * one giant hero number (best score), a row of big supporting figures, a fat
 * horizontal BAR GRAPH of deaths by attack family (replacing the old donut), and
 * a DEATH-LOCATION HEATMAP over the play field ("where you get caught").
 *
 * {@link DeathBars} and {@link DeathHeatMap} are exported — the network view
 * ({@link import('./NetworkStatsView').NetworkStatsView}) renders the same charts
 * over the community aggregates so personal and network stats read alike.
 *
 * ARCHITECTURE: presentational only — holds no app state, talks to no service,
 * imports nothing from `game`/Firebase. The shell fetches the numbers and passes
 * them in. Families are keyed by the canonical {@link AttackFamily} from
 * `core/types` so labels/colours stay in lock-step with the sim's attribution.
 *
 * Note on wording: the persisted field is named `totalDodged` (stable prop
 * contract — the shell wires against it), but every *user-facing* label reads
 * "avoided", matching the Barrage rename.
 */
import type { AttackFamily } from '../core/types';
import { DEATH_HEAT_COLS, DEATH_HEAT_ROWS } from '../services/dataModel';
import { useT } from './i18n';

/**
 * A death count per attack family. Every family is present (zero when the player
 * has never died to it) so the bars render a stable, complete set of six.
 */
export type DeathsByFamily = Record<AttackFamily, number>;

/**
 * The headline survival numbers + death distribution the screen renders.
 * Presentational shape only: the shell derives this from the backend.
 */
export interface PlayerStats {
  /** Total runs played. */
  runs: number;
  /** Best survival score (the leaderboard-relevant number). */
  bestScore: number;
  /** Mean survival score across all runs. */
  averageScore: number;
  /** Longest single survival time (ms) — drawn as a friendly mm:ss.S. */
  longestSurvivalMs: number;
  /** Lifetime hazards avoided (legacy parity). */
  totalDodged: number;
  /** How each attack family has killed the player, keyed by {@link AttackFamily}. */
  deaths: DeathsByFamily;
  /** Death-location heat buckets (server-accumulated; [] before any data). */
  deathHeat: number[];
}

/**
 * The six families in legacy display order — label, icon, and the legacy donut
 * hex colour (analysis §2.9), kept for the bar fills.
 */
const FAMILY_META: ReadonlyArray<{
  key: AttackFamily;
  label: string;
  icon: string;
  color: string;
}> = [
  { key: 'walls', label: 'Walls', icon: '🧱', color: '#5d7794' },
  { key: 'lasers', label: 'Lasers', icon: '⚡', color: '#d35400' },
  { key: 'turret', label: 'Turret', icon: '🎯', color: '#c0392b' },
  { key: 'caro', label: 'Caro', icon: '🌀', color: '#e74c3c' },
  { key: 'missiles', label: 'Missiles', icon: '🚀', color: '#16a085' },
  { key: 'bombs', label: 'Bombs', icon: '💣', color: '#8e44ad' },
];

interface StatsScreenProps {
  stats: PlayerStats;
  /** Back to the previous screen (menu / network shell). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header. */
  embedded?: boolean;
}

/** mm:ss.S from milliseconds (e.g. 92_400 → "1:32.4"). */
function formatDuration(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((totalSeconds * 10) % 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

// ---------------------------------------------------------------------------
// Shared chart pieces (used here AND by NetworkStatsView)
// ---------------------------------------------------------------------------

/**
 * Fat horizontal bars of deaths per attack family — bars scale to the LARGEST
 * family (so the deadliest always fills the track), values show count + share.
 */
export function DeathBars({ deaths }: { deaths: DeathsByFamily }) {
  const t = useT();
  const total = FAMILY_META.reduce((s, f) => s + (deaths[f.key] ?? 0), 0);
  const max = FAMILY_META.reduce((m, f) => Math.max(m, deaths[f.key] ?? 0), 0);
  return (
    <div className="death-bars">
      <style>{DEATH_BARS_CSS}</style>
      {FAMILY_META.map((f) => {
        const value = deaths[f.key] ?? 0;
        const pct = total > 0 ? Math.round((value / total) * 100) : 0;
        const width = max > 0 ? Math.max(value > 0 ? 4 : 0, Math.round((value / max) * 100)) : 0;
        return (
          <div key={f.key} className="death-bar-row" data-zero={value === 0 || undefined}>
            <span className="death-bar-icon" aria-hidden>
              {f.icon}
            </span>
            <span className="death-bar-label">{t('family.' + f.key, f.label)}</span>
            <span className="death-bar-track">
              <span
                className="death-bar-fill"
                style={{ width: `${width}%`, background: f.color }}
              />
            </span>
            <span className="death-bar-value">
              {value.toLocaleString()} <i>{pct}%</i>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Death-location heatmap: the play field as a {@link DEATH_HEAT_COLS}×
 * {@link DEATH_HEAT_ROWS} grid, each cell tinted by its share of deaths (the
 * hottest cell gets a gold ring). Renders the empty label until data accrues.
 */
export function DeathHeatMap({
  heat,
  emptyLabel,
}: {
  heat: readonly number[];
  emptyLabel: string;
}) {
  const size = DEATH_HEAT_COLS * DEATH_HEAT_ROWS;
  const cells = Array.from({ length: size }, (_, i) => heat[i] ?? 0);
  const max = cells.reduce((m, v) => Math.max(m, v), 0);

  if (max === 0) {
    return <p className="death-heat-empty muted-line">{emptyLabel}</p>;
  }

  return (
    <div
      className="death-heat"
      style={{ gridTemplateColumns: `repeat(${DEATH_HEAT_COLS}, 1fr)` }}
      role="img"
      aria-label="Death location heatmap"
    >
      <style>{DEATH_HEAT_CSS}</style>
      {cells.map((v, i) => (
        <span
          key={i}
          className="death-heat-cell"
          data-hot={v === max || undefined}
          title={v > 0 ? `${v}` : undefined}
          style={
            v > 0
              ? { background: `rgba(255, 79, 157, ${(0.1 + 0.8 * (v / max)).toFixed(3)})` }
              : undefined
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The personal stats screen
// ---------------------------------------------------------------------------

export function StatsScreen({ stats, onBack, embedded = false }: StatsScreenProps) {
  const t = useT();
  const totalDeaths = FAMILY_META.reduce((sum, f) => sum + (stats.deaths[f.key] ?? 0), 0);

  return (
    <div className={'screen stats-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{STATS_CSS}</style>

      {!embedded && (
        <header className="network-head stats-head">
          <button className="btn btn-ghost network-back" onClick={onBack}>
            ‹ {t('common.back')}
          </button>
          <h1 className="network-title">{t('nav.stats')}</h1>
        </header>
      )}

      {/* ---- HERO: one giant number + big supporting figures ---- */}
      <section className="panel stats-hero">
        <span className="stats-hero-label">{t('stats.bestScore')}</span>
        <span className="stats-hero-value">{stats.bestScore.toLocaleString()}</span>
        <div className="stats-hero-row">
          <span className="stats-hero-cell">
            <b>{stats.runs.toLocaleString()}</b>
            <i>{t('stats.runs')}</i>
          </span>
          <span className="stats-hero-cell">
            <b>{stats.averageScore.toLocaleString()}</b>
            <i>{t('stats.avgScore')}</i>
          </span>
          <span className="stats-hero-cell">
            <b>{formatDuration(stats.longestSurvivalMs)}</b>
            <i>{t('stats.longestRun')}</i>
          </span>
          <span className="stats-hero-cell">
            <b>{stats.totalDodged.toLocaleString()}</b>
            <i>{t('stats.totalAvoided')}</i>
          </span>
        </div>
      </section>

      {/* ---- Deaths by attack: the fun bar graph ---- */}
      <section className="panel stats-deaths">
        <h2>{t('stats.deathsByAttack')}</h2>
        {totalDeaths === 0 ? (
          <p className="stats-empty muted-line">{t('stats.noDeaths')}</p>
        ) : (
          <DeathBars deaths={stats.deaths} />
        )}
      </section>

      {/* ---- Where you get caught: the death-location heatmap ---- */}
      <section className="panel stats-heatpanel">
        <h2>{t('stats.heatTitle')}</h2>
        <DeathHeatMap heat={stats.deathHeat} emptyLabel={t('stats.heatEmpty')} />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scoped styles
// ---------------------------------------------------------------------------

const STATS_CSS = `
.stats-screen { gap: 14px; }
.stats-screen .stats-head { margin-top: 4px; }
.stats-screen .network-title { font-size: 30px; letter-spacing: 0.08em; }

/* HERO — the big juicy number. */
.stats-screen .stats-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
}
.stats-screen .stats-hero-label {
  font-size: 11px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.16em;
}
.stats-screen .stats-hero-value {
  font-size: 58px;
  font-weight: 900;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(96deg, var(--accent-soft), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 4px 20px var(--accent-glow));
}
.stats-screen .stats-hero-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  width: 100%;
  margin-top: 14px;
}
.stats-screen .stats-hero-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 10px 6px;
  background: var(--panel-2);
  border-radius: var(--radius-sm);
}
.stats-screen .stats-hero-cell b {
  font-size: 20px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  line-height: 1.1;
}
.stats-screen .stats-hero-cell i {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
@media (max-width: 420px) {
  .stats-screen .stats-hero-row { grid-template-columns: repeat(2, 1fr); }
  .stats-screen .stats-hero-value { font-size: 46px; }
}

.stats-screen .stats-empty { margin: 0; font-size: 13px; }
`;

const DEATH_BARS_CSS = `
.death-bars {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 100%;
}
.death-bar-row {
  display: grid;
  grid-template-columns: 24px 68px 1fr auto;
  align-items: center;
  gap: 8px;
}
.death-bar-row[data-zero] { opacity: 0.45; }
.death-bar-icon { font-size: 15px; text-align: center; }
.death-bar-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.death-bar-track {
  height: 14px;
  border-radius: var(--radius-pill);
  background: var(--panel-2);
  overflow: hidden;
}
.death-bar-fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22);
  transition: width var(--t-slow) var(--ease);
}
.death-bar-value {
  min-width: 62px;
  text-align: right;
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}
.death-bar-value i {
  font-style: normal;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--muted);
}
`;

const DEATH_HEAT_CSS = `
.death-heat {
  display: grid;
  gap: 3px;
  width: 100%;
  aspect-ratio: 16 / 9;
  padding: 6px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
}
.death-heat-cell {
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.025);
}
.death-heat-cell[data-hot] {
  box-shadow: inset 0 0 0 2px var(--gold);
}
.death-heat-empty { margin: 0; font-size: 13px; }
`;
